/**
 * PackageStructureEditor - Visual editor for package structure (C4-C9, C12)
 *
 * Inline editor for DRAFT PACKAGE products. Supports:
 * - Add/remove sets with name and selectionRule (C5)
 * - Add LEAF product to set from dropdown (C6)
 * - Dynamic featureValues form based on constraints (C7)
 * - isDefault checkbox per product (C8)
 * - Save with full replace PATCH (C9)
 * - JSON editor toggle (C12)
 *
 * @see docs/specifications/2026-03-20-package-structure-visual-editor.md
 */
import React, { useState, useCallback } from "react";
import type {
  EnrichedPackageStructureSet,
  EnrichedPackageStructure,
} from "../../../../types/adminBilling";
import {
  useLeafProductsQuery,
  useUpdateProductMutation,
} from "../../../../hooks/useAdminProductCatalog";
import { useToast } from "../../../../contexts/ToastContext";
import {
  ALL_SELECTION_RULES,
  MAX_SETS_PER_PACKAGE,
} from "./constants";
import { EditableSetCard } from "./EditableSetCard";

// =============================================================================
// TYPES
// =============================================================================

interface PackageStructureEditorProps {
  productId: string;
  brandId: string;
  initialStructure: EnrichedPackageStructure | null;
  onSaved: () => void;
  onCancel: () => void;
}

/** Set with a temporary ID for React key stability (not sent to backend) */
export interface EditorSet extends EnrichedPackageStructureSet {
  tempId: string;
}

interface EditorStructure {
  sets: EditorSet[];
}

// =============================================================================
// HELPERS
// =============================================================================

const generateTempId = (): string =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : Date.now().toString() + Math.random().toString(36).slice(2);

/** Creates an empty package structure */
const createEmptyStructure = (): EditorStructure => ({
  sets: [],
});

/** Creates a new empty set */
const createEmptySet = (): EditorSet => ({
  tempId: generateTempId(),
  name: "",
  selectionRule: "ALL",
  products: [],
});

/** Adds tempId to sets coming from the backend */
const toEditorStructure = (structure: EnrichedPackageStructure): EditorStructure => ({
  sets: structure.sets.map((s) => ({ ...s, tempId: generateTempId() })),
});

/**
 * Strips enrichment fields (productName, tempId) from structure before sending to backend.
 * Backend expects the stored format without productName or tempId.
 */
const stripEnrichmentFields = (
  structure: EditorStructure
): Record<string, unknown> => ({
  sets: structure.sets.map((set) => ({
    name: set.name,
    selectionRule: set.selectionRule,
    products: set.products.map((p) => ({
      productId: p.productId,
      isDefault: p.isDefault,
      ...(p.featureValues && Object.keys(p.featureValues).length > 0
        ? { featureValues: p.featureValues }
        : {}),
    })),
  })),
});

/** Gets all productIds currently used in the structure */
const getUsedProductIds = (structure: EditorStructure): Set<string> => {
  const ids = new Set<string>();
  for (const set of structure.sets) {
    for (const product of set.products) {
      ids.add(product.productId);
    }
  }
  return ids;
};

// =============================================================================
// MAIN EDITOR (C4, C9, C12)
// =============================================================================

export const PackageStructureEditor: React.FC<PackageStructureEditorProps> = ({
  productId,
  brandId,
  initialStructure,
  onSaved,
  onCancel,
}) => {
  const { addToast } = useToast();
  const updateMutation = useUpdateProductMutation(productId, brandId, { silent: true });

  // Visual editor state
  const [structure, setStructure] = useState<EditorStructure>(
    initialStructure ? toEditorStructure(initialStructure) : createEmptyStructure()
  );

  // JSON editor state (C12)
  const [jsonMode, setJsonMode] = useState(false);
  const [jsonText, setJsonText] = useState("");

  // Leaf products for picker (C6)
  const { data: leafProducts, isLoading: isLoadingLeaves } =
    useLeafProductsQuery(brandId);

  const usedProductIds = getUsedProductIds(structure);

  // ----- Set operations (C5) -----

  const handleAddSet = () => {
    setStructure((prev) => ({
      ...prev,
      sets: [...prev.sets, createEmptySet()],
    }));
  };

  const handleUpdateSet = (index: number, updated: EditorSet) => {
    setStructure((prev) => ({
      ...prev,
      sets: prev.sets.map((s, i) => (i === index ? updated : s)),
    }));
  };

  const handleRemoveSet = (index: number) => {
    setStructure((prev) => ({
      ...prev,
      sets: prev.sets.filter((_, i) => i !== index),
    }));
  };

  // ----- JSON mode toggle (C12) -----

  const handleSwitchToJson = useCallback(() => {
    setJsonText(JSON.stringify(stripEnrichmentFields(structure), null, 2));
    setJsonMode(true);
  }, [structure]);

  const handleSwitchToVisual = useCallback(() => {
    try {
      const parsed = JSON.parse(jsonText);
      if (!parsed.sets || !Array.isArray(parsed.sets)) {
        addToast({
          message: "Nieprawidłowa struktura JSON: brakuje tablicy 'sets'",
          type: "error",
        });
        return;
      }
      // Validate structural integrity of each set
      const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      for (const set of parsed.sets) {
        if (set.selectionRule && !ALL_SELECTION_RULES.includes(set.selectionRule)) {
          addToast({
            message: `Nieprawidłowa reguła wyboru: "${set.selectionRule}". Dozwolone: ${ALL_SELECTION_RULES.join(", ")}`,
            type: "error",
          });
          return;
        }
        if (Array.isArray(set.products)) {
          for (const p of set.products) {
            if (p.productId && !UUID_RE.test(p.productId)) {
              addToast({
                message: `Nieprawidłowy productId: "${p.productId}". Oczekiwany format UUID.`,
                type: "error",
              });
              return;
            }
          }
        }
      }
      setStructure(toEditorStructure(parsed as EnrichedPackageStructure));
      setJsonMode(false);
    } catch {
      addToast({
        message: "Nieprawidłowy JSON. Popraw błędy lub pozostań w trybie JSON.",
        type: "error",
      });
    }
  }, [jsonText, addToast]);

  // ----- Save (C9) -----

  const handleSave = () => {
    let packageStructureToSave: Record<string, unknown>;

    if (jsonMode) {
      try {
        packageStructureToSave = JSON.parse(jsonText);
      } catch {
        addToast({ message: "Nieprawidłowy JSON", type: "error" });
        return;
      }
    } else {
      packageStructureToSave = stripEnrichmentFields(structure);
    }

    updateMutation.mutate(
      { packageStructure: packageStructureToSave },
      {
        onSuccess: () => {
          addToast({
            message: "Struktura pakietu została zapisana",
            type: "success",
          });
          onSaved();
        },
        onError: (error: unknown) => {
          const axiosError = error as {
            response?: { data?: { message?: string } };
          };
          const message =
            axiosError.response?.data?.message ?? "Nie udało się zapisać";
          addToast({ message, type: "error" });
        },
      }
    );
  };

  const isSaving = updateMutation.isPending;
  const hasEmptySetNames = !jsonMode && structure.sets.some((s) => !s.name.trim());

  // ----- Render -----

  return (
    <div className="space-y-4">
      {/* Mode toggle (C12) */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-neutral-900">
          Edycja struktury pakietu
        </h4>
        <button
          type="button"
          onClick={jsonMode ? handleSwitchToVisual : handleSwitchToJson}
          className="text-xs text-primary-600 hover:text-primary-800 font-medium"
        >
          {jsonMode ? "Edytor wizualny" : "Edytuj jako JSON"}
        </button>
      </div>

      {jsonMode ? (
        /* JSON editor (C12) */
        <textarea
          value={jsonText}
          onChange={(e) => setJsonText(e.target.value)}
          rows={16}
          className="w-full border border-neutral-300 rounded-md px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-primary-500"
          spellCheck={false}
        />
      ) : (
        /* Visual editor */
        <>
          {isLoadingLeaves && (
            <div className="flex items-center gap-2 text-xs text-neutral-500">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
              Ładowanie produktów...
            </div>
          )}

          <div className="space-y-3">
            {structure.sets.map((set, index) => (
              <EditableSetCard
                key={set.tempId}
                set={set}
                setIndex={index}
                leafProducts={leafProducts ?? []}
                usedProductIds={usedProductIds}
                onUpdate={(updated) => handleUpdateSet(index, updated)}
                onRemove={() => handleRemoveSet(index)}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={handleAddSet}
            disabled={structure.sets.length >= MAX_SETS_PER_PACKAGE}
            className="text-xs text-primary-600 hover:text-primary-800 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            + Dodaj set ({structure.sets.length}/{MAX_SETS_PER_PACKAGE})
          </button>
        </>
      )}

      {/* Actions: Save / Cancel (C9) */}
      <div className="flex justify-end gap-3 pt-2 border-t border-neutral-200">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSaving}
          className="px-4 py-2 text-sm text-neutral-600 hover:text-neutral-800 font-medium transition-colors"
        >
          Anuluj
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving || hasEmptySetNames}
          className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors inline-flex items-center gap-2"
        >
          {isSaving && (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          )}
          {isSaving ? "Zapisywanie..." : "Zapisz"}
        </button>
      </div>
    </div>
  );
};
